import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2, Plus, Trash2 } from "lucide-react";

import { saveRuleTiers, type KigTier, type KigTierKind, type StagedPlanRule } from "@/api/client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Stepped-KIG tier editor (Opcija A review-UI).
 *
 * Some plans set KIG as a function of plot area, not a flat coefficient (Poreč
 * čl.56/103, Umag čl.96). The extractor can't reliably read those nested tables
 * (amendment overstrikes), so the architect curates the bands here against the
 * verbatim citation. The same continuity invariant the backend enforces is
 * shown live: each band's value at its lower edge must equal the previous band's
 * value there ("zbir" carry-over) — a mismatch flags a transcription error.
 */

const KIND_LABELS: Record<KigTierKind, string> = {
  pct: "postotak čestice",
  base_marginal: "zbir + % iznad praga",
  abs: "apsolutni m² (cap)",
};

function maxFootprint(band: KigTier, area: number): number | null {
  if (band.kind === "pct") return band.pct != null ? band.pct * area : null;
  if (band.kind === "base_marginal") {
    if (band.base_m2 == null || band.pct == null) return null;
    return band.base_m2 + band.pct * Math.max(0, area - (band.above_m2 ?? 0));
  }
  if (band.kind === "abs") return band.max_m2 ?? null;
  return null;
}

function bandForArea(tiers: KigTier[], area: number): KigTier | null {
  const ordered = [...tiers].sort((a, b) => (a.plot_min_m2 ?? 0) - (b.plot_min_m2 ?? 0));
  for (const b of ordered) {
    const lo = b.plot_min_m2 ?? 0;
    const hi = b.plot_max_m2;
    if (area < lo) continue;
    if (hi == null || area < hi) return b;
  }
  return ordered[ordered.length - 1] ?? null;
}

function continuityWarnings(tiers: KigTier[]): string[] {
  const ordered = [...tiers].sort((a, b) => (a.plot_min_m2 ?? 0) - (b.plot_min_m2 ?? 0));
  const out: string[] = [];
  for (let i = 1; i < ordered.length; i++) {
    const x = ordered[i].plot_min_m2;
    if (x == null) continue;
    const prev = maxFootprint(ordered[i - 1], x);
    const cur = maxFootprint(ordered[i], x);
    if (prev == null || cur == null || Math.abs(prev - cur) > 0.6) {
      out.push(`Prekid na ${x} m²: ${prev ?? "?"} m² → ${cur ?? "?"} m²`);
    }
  }
  return out;
}

const NEW_BAND: KigTier = { plot_min_m2: 0, plot_max_m2: null, kind: "pct", pct: 0.3 };

export function KigTierEditor({
  rule,
  planId,
  onSaved,
}: {
  rule: StagedPlanRule;
  planId: number;
  onSaved: (fields: { kig_tiers: KigTier[] | null; geo_scope: string | null; kig_max: number | null }) => void;
}) {
  const [open, setOpen] = useState(Boolean(rule.kig_tiers?.length));
  const [tiers, setTiers] = useState<KigTier[]>(rule.kig_tiers ?? []);
  const [geoScope, setGeoScope] = useState<string>(rule.geo_scope ?? "");
  const [probe, setProbe] = useState<string>("1000");
  const [showCitation, setShowCitation] = useState(false);

  const warnings = useMemo(() => continuityWarnings(tiers), [tiers]);
  const probeArea = Number(probe.replace(",", "."));
  const probeResult = useMemo(() => {
    if (!tiers.length || !Number.isFinite(probeArea) || probeArea <= 0) return null;
    const band = bandForArea(tiers, probeArea);
    if (!band) return null;
    const foot = maxFootprint(band, probeArea);
    if (foot == null) return null;
    return { foot, kig: foot / probeArea };
  }, [tiers, probeArea]);

  const save = useMutation({
    mutationFn: () =>
      saveRuleTiers(planId, rule.id, {
        kig_tiers: tiers.length ? tiers : null,
        geo_scope: geoScope || null,
      }),
    onSuccess: (resp) => onSaved({ kig_tiers: resp.kig_tiers, geo_scope: resp.geo_scope, kig_max: resp.kig_max }),
  });

  const patchBand = (i: number, patch: Partial<KigTier>) =>
    setTiers((t) => t.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const num = (v: string): number | null => {
    const n = Number(v.replace(",", "."));
    return v.trim() === "" || !Number.isFinite(n) ? null : n;
  };

  return (
    <div className="mt-2 border-t border-dashed border-stone-200 pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-[0.74rem] font-semibold text-ind-700 hover:text-ind-900"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Stepenasti KIG (po površini čestice)
        {rule.kig_tiers?.length ? (
          <span className="ml-1 rounded bg-ind-100 px-1.5 text-[0.68rem] text-ind-800">
            {rule.kig_tiers.length} bandova
          </span>
        ) : null}
      </button>

      {open && (
        <div className="mt-2 rounded-md bg-stone-50 p-2">
          <div className="mb-2 flex items-center gap-2">
            <label className="text-[0.74rem] text-stone-600">Geografski režim:</label>
            <select
              className="rounded border border-stone-300 bg-white px-2 py-1 text-[0.74rem]"
              value={geoScope}
              onChange={(e) => setGeoScope(e.target.value)}
            >
              <option value="">univerzalno (cijela općina)</option>
              <option value="obalno">obalno područje</option>
              <option value="izvan_obalnog">izvan obalnog područja</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            {tiers.map((band, i) => (
              <div key={i} className="flex flex-wrap items-center gap-1 text-[0.72rem]">
                <span className="text-stone-500">od</span>
                <input
                  className="w-16 rounded border border-stone-300 px-1 py-0.5"
                  value={band.plot_min_m2 ?? ""}
                  onChange={(e) => patchBand(i, { plot_min_m2: num(e.target.value) ?? 0 })}
                  placeholder="0"
                />
                <span className="text-stone-500">do</span>
                <input
                  className="w-16 rounded border border-stone-300 px-1 py-0.5"
                  value={band.plot_max_m2 ?? ""}
                  onChange={(e) => patchBand(i, { plot_max_m2: num(e.target.value) })}
                  placeholder="∞"
                />
                <span className="text-stone-500">m² →</span>
                <select
                  className="rounded border border-stone-300 bg-white px-1 py-0.5"
                  value={band.kind}
                  onChange={(e) => patchBand(i, { kind: e.target.value as KigTierKind })}
                >
                  {(Object.keys(KIND_LABELS) as KigTierKind[]).map((k) => (
                    <option key={k} value={k}>{KIND_LABELS[k]}</option>
                  ))}
                </select>
                {band.kind === "pct" && (
                  <input
                    className="w-16 rounded border border-stone-300 px-1 py-0.5"
                    value={band.pct ?? ""}
                    onChange={(e) => patchBand(i, { pct: num(e.target.value) })}
                    placeholder="0.30"
                    title="udio (0–1), npr. 0.30 za 30%"
                  />
                )}
                {band.kind === "base_marginal" && (
                  <>
                    <input className="w-16 rounded border border-stone-300 px-1 py-0.5" value={band.base_m2 ?? ""}
                      onChange={(e) => patchBand(i, { base_m2: num(e.target.value) })} placeholder="zbir m²" title="bazni m²" />
                    <span className="text-stone-500">+</span>
                    <input className="w-14 rounded border border-stone-300 px-1 py-0.5" value={band.pct ?? ""}
                      onChange={(e) => patchBand(i, { pct: num(e.target.value) })} placeholder="0.25" title="udio iznad praga" />
                    <span className="text-stone-500">iznad</span>
                    <input className="w-16 rounded border border-stone-300 px-1 py-0.5" value={band.above_m2 ?? ""}
                      onChange={(e) => patchBand(i, { above_m2: num(e.target.value) })} placeholder="prag m²" title="prag m²" />
                  </>
                )}
                {band.kind === "abs" && (
                  <input className="w-20 rounded border border-stone-300 px-1 py-0.5" value={band.max_m2 ?? ""}
                    onChange={(e) => patchBand(i, { max_m2: num(e.target.value) })} placeholder="385" title="apsolutni cap m²" />
                )}
                <button
                  type="button"
                  onClick={() => setTiers((t) => t.filter((_, j) => j !== i))}
                  className="rounded p-0.5 text-stone-400 hover:bg-red-100 hover:text-red-600"
                  title="Ukloni band"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setTiers((t) => [...t, { ...NEW_BAND }])}
            className="mt-1 flex items-center gap-1 text-[0.72rem] text-ind-700 hover:text-ind-900"
          >
            <Plus className="h-3.5 w-3.5" /> dodaj band
          </button>

          {warnings.length > 0 && (
            <ul className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[0.7rem] text-amber-800">
              {warnings.map((w, i) => (
                <li key={i}>⚠ {w} — provjeri brojke (kontinuitet bandova).</li>
              ))}
            </ul>
          )}

          {tiers.length > 0 && (
            <div className="mt-2 flex items-center gap-2 text-[0.72rem] text-stone-600">
              <span>provjeri na</span>
              <input
                className="w-20 rounded border border-stone-300 px-1 py-0.5"
                value={probe}
                onChange={(e) => setProbe(e.target.value)}
              />
              <span>m² →</span>
              {probeResult ? (
                <span className="font-semibold text-stone-800">
                  max {probeResult.foot.toFixed(0)} m² (KIG {(probeResult.kig * 100).toFixed(1).replace(".", ",")} %)
                </span>
              ) : (
                <span className="text-stone-400">—</span>
              )}
            </div>
          )}

          {rule.source_text && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowCitation((s) => !s)}
                className="text-[0.7rem] text-stone-500 underline hover:text-stone-700"
              >
                {showCitation ? "sakrij" : "prikaži"} citat članka
              </button>
              {showCitation && (
                <pre className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap rounded border border-stone-200 bg-white p-2 text-[0.68rem] leading-snug text-stone-600">
                  {rule.source_text}
                </pre>
              )}
            </div>
          )}

          <div className="mt-2 flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={save.isPending || (tiers.length > 0 && warnings.length > 0)}
              onClick={() => save.mutate()}
            >
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {tiers.length ? "Spremi tierove" : "Ukloni tierove (ravni KIG)"}
            </Button>
            {save.isError && (
              <span className="text-[0.7rem] text-red-600">
                {save.error instanceof Error ? save.error.message : "Greška pri spremanju."}
              </span>
            )}
            {save.isSuccess && !save.isPending && (
              <span className={cn("text-[0.7rem]", "text-green-700")}>Spremljeno ✓</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
